import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Tooltip,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { ArrowBack, Edit, Delete, Person, EmojiEvents as EmojiEventsIcon, MoreVert, Visibility } from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { storyService, userService } from '../services/api';
import Comments from '../components/Comments';
import JudgingComments from '../components/JudgingComments';

const StoryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authorStats, setAuthorStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    const fetchStory = async () => {
      try {
        setLoading(true);
        console.log('StoryDetail: Fetching story with ID:', id);
        const response = await storyService.getStory(id);
        console.log('StoryDetail: Full response:', response);
        console.log('StoryDetail: Response data:', response.data);
        console.log('StoryDetail: Response data type:', typeof response.data);
        
        // Try both possible response structures
        const storyData = response.data?.data || response.data;
        console.log('StoryDetail: Final story data:', storyData);
        
        setStory(storyData);
      } catch (err) {
        setError('Failed to load story');
        console.error('Error fetching story:', err);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchStory();
    }
  }, [id]);

  // Fetch author stats when story loads
  useEffect(() => {
    if (story?.author_id) {
      fetchAuthorStats(story.author_id);
    }
  }, [story?.author_id]);

  const handleEdit = () => {
    handleMenuClose();
    navigate(`/edit/${id}`);
  };

  const handleDelete = async () => {
    handleMenuClose();
    if (window.confirm('Are you sure you want to delete this story?')) {
      try {
        await storyService.deleteStory(id);
        navigate('/stories');
      } catch (err) {
        setError('Failed to delete story');
      }
    }
  };

  const fetchAuthorStats = async (authorId) => {
    if (!authorId || statsLoading) return;
    
    try {
      setStatsLoading(true);
      const response = await userService.getUserStats(authorId);
      console.log('Author stats response:', response);
      
      // Handle double nesting: response.data.data contains the actual stats
      const statsData = response.data?.data || response.data;
      console.log('Processed stats data:', statsData);
      setAuthorStats(statsData);
    } catch (error) {
      console.error('Error fetching author stats:', error);
      // Don't show error to user, just don't display stats
      setAuthorStats(null);
    } finally {
      setStatsLoading(false);
    }
  };

  const getAuthorName = (story, currentUser) => {
    // Debug: Log what's available in the user session
    console.log('StoryDetail: Current user session data:', currentUser);
    console.log('StoryDetail: Story author data:', story.author);
    console.log('StoryDetail: Story author_id:', story.author_id);
    console.log('StoryDetail: Current user userId:', currentUser?.userId);
    console.log('StoryDetail: Current user sub:', currentUser?.sub);
    
    // If this is the current user's story, use their session data first
    const currentUserId = currentUser?.userId || currentUser?.sub;
    if (story.author_id === currentUserId) {
      // Priority 1: Use Cognito session data
      let sessionName = '';
      
      // Try full name first (Google OAuth format)
      if (currentUser.name) {
        sessionName = currentUser.name.trim();
      } else {
        // Try individual fields
        sessionName = `${currentUser.given_name || ''} ${currentUser.family_name || ''}`.trim();
      }
      
      console.log('StoryDetail: Session name:', sessionName);
      if (sessionName) {
        return sessionName;
      }
      
      // Priority 2: Use profile data from database
      if (story.author) {
        const profileName = `${story.author.given_name || ''} ${story.author.family_name || ''}`.trim();
        console.log('StoryDetail: Profile name:', profileName);
        if (profileName) {
          return profileName;
        }
      }
      
      // Fallback: Show "You" for own stories
      return 'You';
    } else {
      // For other users' stories, use the looked-up author data
      if (story.author) {
        const authorName = `${story.author.given_name || ''} ${story.author.family_name || ''}`.trim();
        if (authorName) {
          return authorName;
        }
      }
      
      // Fallback: Anonymous
      return 'Anonymous';
    }
  };

  const canEdit = user && story && (user.sub === story.author_id || user.role === 'admin');

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
          sx={{ mt: 2 }}
        >
          Back
        </Button>
      </Container>
    );
  }

  if (!story) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="info">Story not found</Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
          sx={{ mt: 2 }}
        >
          Back
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Back
        </Button>
      </Box>

      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h3" component="h1" gutterBottom>
              {story.title}
            </Typography>
            
            {/* Compact Author and Metadata Row */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2, 
              flexWrap: 'wrap',
              mb: 2
            }}>
              <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Person fontSize="small" />
                <Tooltip 
                title={
                  <Box sx={{ p: 1.5, maxWidth: 500 }}>
                    {/* Author Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar sx={{ width: 48, height: 48, mr: 2 }}>
                        {(() => {
                          // Try multiple sources for the author picture
                          const pictureUrl = story.author?.picture || 
                                           story.author?.avatar_url || 
                                           story.author?.attributes?.picture;
                          
                          return pictureUrl ? (
                            <img 
                              src={pictureUrl} 
                              alt="" 
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              onError={(e) => {
                                console.log('Author avatar failed to load:', pictureUrl);
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <Person />
                          );
                        })()}
                      </Avatar>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'inherit' }}>
                          {story.author?.given_name && story.author?.family_name 
                            ? `${story.author.given_name} ${story.author.family_name}`.trim()
                            : story.author?.given_name || 'Anonymous'
                          }
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', opacity: 0.8 }}>
                          Story Author
                        </Typography>
                      </Box>
                    </Box>

                    {/* Bio Section */}
                    {story.author?.bio && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'inherit' }}>
                          About the Author
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            lineHeight: 1.5,
                            color: 'inherit',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                          }}
                        >
                          {story.author.bio}
                        </Typography>
                      </Box>
                    )}

                    {/* Statistics Section */}
                    <Box sx={{ 
                      borderTop: '1px solid', 
                      borderColor: 'divider', 
                      pt: 1.5
                    }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5, color: 'inherit' }}>
                        Author Statistics
                      </Typography>
                      
                      {statsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                          <CircularProgress size={24} />
                        </Box>
                      ) : authorStats ? (
                        <Box sx={{ 
                          display: 'grid',
                          gridTemplateColumns: 'repeat(2, 1fr)',
                          gap: 1.5
                        }}>
                          {/* Published Stories */}
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                              {authorStats.stories_published || 0}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Published Stories
                            </Typography>
                          </Box>
                          
                          {/* Total Views */}
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                              {authorStats.total_story_views || 0}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Total Views
                            </Typography>
                          </Box>
                          
                          {/* Comments Made */}
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                              {authorStats.comments_made || 0}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Comments Made
                            </Typography>
                          </Box>
                          
                          {/* Active Days */}
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                              {authorStats.active_days || 0}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Active Days
                            </Typography>
                          </Box>
                          
                          {/* Average Words per Story */}
                          {authorStats.computed?.average_story_words > 0 && (
                            <Box sx={{ textAlign: 'center', gridColumn: 'span 2' }}>
                              <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                                {authorStats.computed.average_story_words}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Avg Words per Story
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      ) : (
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontStyle: 'italic', 
                            color: 'text.secondary',
                            opacity: 0.7,
                            textAlign: 'center',
                            py: 1
                          }}
                        >
                          Statistics not available
                        </Typography>
                      )}
                    </Box>

                    {/* Last Activity (if available) */}
                    {authorStats?.last_activity && (
                      <Typography variant="caption" color="text.secondary" sx={{ 
                        display: 'block', 
                        textAlign: 'center', 
                        mt: 1.5,
                        fontStyle: 'italic'
                      }}>
                        Last active: {formatDistanceToNow(new Date(authorStats.last_activity), { addSuffix: true })}
                      </Typography>
                    )}
                  </Box>
                }
                placement="bottom-start"
                arrow
                PopperProps={{
                  sx: {
                    '& .MuiTooltip-tooltip': {
                      backgroundColor: 'background.paper',
                      color: 'text.primary',
                      boxShadow: 4,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      maxWidth: 550,
                      minWidth: 400,
                      '& .MuiTooltip-arrow': {
                        color: 'background.paper',
                        '&::before': {
                          border: '1px solid',
                          borderColor: 'divider',
                        }
                      }
                    }
                  }
                }}
              >
                <Typography 
                  component="span"
                  sx={{ 
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    textDecorationStyle: 'dotted',
                    '&:hover': { 
                      color: 'primary.main',
                      textDecorationStyle: 'solid'
                    },
                    transition: 'color 0.2s ease'
                  }}
                >
                  {getAuthorName(story, user)}
                </Typography>
              </Tooltip>
              </Typography>
              
              <Typography variant="body2" color="text.secondary">
                📅 {new Date(story.created_at).toLocaleDateString()}
              </Typography>
              
              <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Visibility fontSize="small" />
                {story.view_count || 0} views
              </Typography>
              
              {story.word_count && (
                <Typography variant="body2" color="text.secondary">
                  📝 {story.word_count} words
                </Typography>
              )}
            </Box>

            {/* Tags Row */}
            {story.tags && story.tags.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                {story.tags.map((tag, index) => (
                  <Chip
                    key={index}
                    label={tag}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.75rem', height: 24 }}
                  />
                ))}
              </Box>
            )}

            {/* Event Badge */}
            {story.event_id && story.event && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <EmojiEventsIcon fontSize="small" color="primary" />
                <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
                  {story.event.title}
                </Typography>
              </Box>
            )}
          </Box>
          
          {canEdit && (
            <Box>
              <IconButton
                aria-label="more actions"
                aria-controls={menuOpen ? 'story-actions-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={menuOpen ? 'true' : undefined}
                onClick={handleMenuClick}
                size="small"
              >
                <MoreVert />
              </IconButton>
              <Menu
                id="story-actions-menu"
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
                MenuListProps={{
                  'aria-labelledby': 'story-actions-button',
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <MenuItem onClick={handleEdit}>
                  <ListItemIcon>
                    <Edit fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Edit Story</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
                  <ListItemIcon>
                    <Delete fontSize="small" sx={{ color: 'error.main' }} />
                  </ListItemIcon>
                  <ListItemText>Delete Story</ListItemText>
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Typography
          variant="body1"
          component="div"
          sx={{
            whiteSpace: 'pre-wrap',
            lineHeight: 1.8,
            fontSize: '1.1rem'
          }}
        >
          {story.content}
        </Typography>

        <Divider sx={{ mt: 4, mb: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Status: {story.status === 'published' ? 'Published' : 'Draft'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Views: {story.view_count || 0} • Comments: {story.comment_count || 0}
          </Typography>
        </Box>
      </Paper>

      {/* Judge Feedback Section - Only visible to story author */}
      {user && story && (user.user_id === story.author_id || user.sub === story.author_id || user.userId === story.author_id) && story.event_id && (
        <Box sx={{ mt: 6, mb: 6 }}>
          <JudgingComments storyId={id} storyTitle={story.title} />
        </Box>
      )}

      {/* Comments Section */}
      <Comments storyId={id} />
    </Container>
  );
};

export default StoryDetail;
